<template>
  <div class="relative">
    <div
      v-if="!profileImageUrl"
      :class="`rounded-full element-shadow flex justify-center items-center font-black size-${size}`"
      :style="{
        background: circleBackground,
        color: circleForeground,
      }"
    >
      <div>
        {{ `${user.get('firstname').substring(0, 1)}${user.get('lastname').substring(0, 1)}` }}
      </div>
    </div>
    <div v-else :class="`rounded-full size-${size}`">
      <img
        :key="uniqueKey"
        ref="imageRef"
        alt="profileImage"
        :class="`rounded-full aspect-square object-cover size-${size}`"
        :src="profileImageUrl"
      />
    </div>
    <div
      v-if="canEdit && !isUploading"
      class="absolute right-0 bottom-0 bg-white shadow flex items-center text-primary gap-2 border cursor-pointer hover:bg-gray-100 transition-all duration-300 justify-center rounded-full w-8 h-8 p-1"
      @click="onPhoto"
    >
      <i class="material-icons text-base">photo</i>
    </div>
    <div
      v-if="isUploading"
      class="absolute top-0 right-0 w-full h-full bg-white rounded-full border border-primary z-10 flex justify-center items-center"
    >
      <i class="material-icons text-3xl text-primary animate-bounce">hourglass_empty</i>
    </div>
  </div>
</template>

<script setup>
import Parse from 'parse';
import { hexToRgb } from '@/js/color-helper.js';
import { computed, toRefs, ref, onUnmounted } from 'vue';
import { actionSheetController } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { getCurrentInstance } from 'vue';

const instance = getCurrentInstance();

const emitter = instance.appContext.config.globalProperties.emitter;
const toast = instance.appContext.config.globalProperties.$toast;

const { t } = useI18n();

const uniqueKey = ref(0);
const imageRef = ref(null);
const isUploading = ref(false);

const props = defineProps({
  user: { type: Parse.User, required: true },
  canEdit: { type: Boolean, default: false },
  size: {
    type: String,
    default: 'xl',
    validator(value) {
      return ['sm', 'lg', 'xl'].includes(value);
    },
  },
});

const { user } = toRefs(props);

// Local override so we can reflect a new/removed image WITHOUT mutating or
// refetching the (deeply-reactive) Parse user object, which locks the main thread.
// undefined = no override (read from the Parse object); null = removed; string = url.
const localImageUrl = ref(undefined);

const profileImageUrl = computed(() => {
  if (localImageUrl.value !== undefined) return localImageUrl.value;
  const img = user.value.get('profileImage');
  return img ? img.url() : null;
});

const circleBackground = computed(() => {
  return user.value.get('color') || this.$store.getters['company-style/companyStyle'].primary;
});

const circleForeground = computed(() => {
  return hexToRgb(circleBackground.value);
});

const onPhoto = async () => {
  const actionSheet = await actionSheetController.create({
    header: t('upload_profileimage'),
    buttons: [
      {
        text: t('camera'),
        data: {
          action: 'camera',
        },
      },
      {
        text: t('photo_library'),
        data: {
          action: 'library',
        },
      },
      {
        text: t('delete_profile_image'),
        role: 'destructive',
        data: {
          action: 'delete',
        },
      },
      {
        text: t('cancel_1'),
        role: 'cancel',
        data: {
          action: 'cancel',
        },
      },
    ],
  });

  await actionSheet.present();

  const {
    data: { action },
  } = await actionSheet.onDidDismiss();

  if (action === 'camera') {
    await onCamera();
  }
  if (action === 'library') {
    await onPhotoLibrary();
  }
  if (action === 'delete') {
    isUploading.value = true;
    try {
      await Parse.Cloud.run('setUserProfileImage', { userId: user.value.id });
      // Reflect removal locally WITHOUT touching the reactive Parse object.
      localImageUrl.value = null;
      uniqueKey.value += 1;
      // Notify other instances showing this user (e.g. the sidebar avatar).
      emitter.emit('profileImageChanged', { userId: user.value.id, url: null });
    } catch (e) {
      console.error('Failed to delete profile image:', e);
      toast?.error?.(e?.message || 'Failed to delete profile image');
    } finally {
      isUploading.value = false;
    }
  }
};

const onCamera = async () => {
  const image = await Camera.getPhoto({
    quality: 100,
    allowEditing: true,
    width: 1000,
    height: 1000,
    resultType: CameraResultType.Base64,
    source: CameraSource.Camera,
  });
  await saveImage(image);
};

const onPhotoLibrary = async () => {
  const image = await Camera.getPhoto({
    quality: 100,
    allowEditing: true,
    width: 1000,
    height: 1000,
    resultType: CameraResultType.Base64,
    source: CameraSource.Photos,
  });
  await saveImage(image);
};

const saveImage = async ({ base64String, format }) => {
  isUploading.value = true;
  try {
    const result = await Parse.Cloud.run('setUserProfileImage', {
      userId: user.value.id,
      base64: base64String,
      format,
    });
    // Reflect the new image locally WITHOUT touching the reactive Parse object.
    localImageUrl.value = result?.profileImageUrl || null;
    uniqueKey.value += 1;
    // Notify other instances showing this user (e.g. the sidebar avatar).
    emitter.emit('profileImageChanged', { userId: user.value.id, url: localImageUrl.value });
  } catch (e) {
    console.error('Failed to save profile image:', e);
    toast?.error?.(e?.message || 'Failed to save profile image');
  } finally {
    isUploading.value = false;
  }
};

const onPhotoChanged = (payload) => {
  // Only react when the broadcast concerns the user this instance renders.
  if (!payload || payload.userId !== user.value.id) return;
  // Apply the new/removed image URL so instances that did not perform the
  // upload (e.g. the sidebar avatar) reflect the change without a page reload.
  // Updating the reactive refs is enough to re-render — NO $forceUpdate(), which
  // would force a synchronous re-render (and a deep read of the reactive Parse
  // object) on every listening instance.
  if ('url' in payload) {
    localImageUrl.value = payload.url;
  }
  uniqueKey.value += 1;
};

emitter.on('profileImageChanged', onPhotoChanged);
// IMPORTANT: mitt has no auto-cleanup. UserProfileImage is rendered once per
// post and per comment in the feed, so without this every avatar would leak a
// listener; a single emit would then fan out to thousands of handlers and hang
// the tab. Remove the listener when this instance unmounts.
onUnmounted(() => emitter.off('profileImageChanged', onPhotoChanged));
</script>

<style scoped>
.size-sm {
  @apply w-14 h-14 text-lg;
}
.size-lg {
  @apply w-20 h-20 text-2xl;
}

.size-xl {
  @apply w-40 h-40 text-5xl;
}
</style>
